import React, { useState } from 'react';
import { apiPost } from './apiUtils';
import { validateEmail, sanitizeRetailerCode, validateRetailerCode, sanitizeInput } from './authUtils';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Button } from './components/ui/button';
import { Alert, AlertDescription } from './components/ui/alert';
import { Wine, CheckCircle2, XCircle } from 'lucide-react';

function Signup({ onLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [shopName, setShopName] = useState('');
  const [retailerCode, setRetailerCode] = useState('');
  const [address, setAddress] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Password validation helper
  const getPasswordValidation = (pwd) => {
    return {
      length: pwd.length >= 8,
      lowercase: /(?=.*[a-z])/.test(pwd),
      uppercase: /(?=.*[A-Z])/.test(pwd),
      number: /(?=.*\d)/.test(pwd)
    };
  };

  const handleSignup = async () => {
    setLoading(true);
    setError('');
    
    // Client-side validation
    const cleanName = sanitizeInput(name);
    const cleanEmail = email.trim();
    const cleanShopName = sanitizeInput(shopName);
    const cleanRetailerCode = sanitizeRetailerCode(retailerCode);
    const cleanAddress = sanitizeInput(address);
    const cleanLicenseNumber = sanitizeInput(licenseNumber);
    
    // Validation checks
    if (!cleanName) {
      setError('Name is required');
      setLoading(false);
      return;
    }
    
    if (!validateEmail(cleanEmail)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    
    // Enhanced password validation to match server requirements
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      setLoading(false);
      return;
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      setLoading(false);
      return;
    }
    
    if (!/(?=.*\d)/.test(password)) {
      setError('Password must contain at least one number');
      setLoading(false);
      return;
    }
    
    if (!cleanShopName) {
      setError('Shop name is required');
      setLoading(false);
      return;
    }
    
    if (!validateRetailerCode(cleanRetailerCode)) {
      setError('Retailer code must be 7 digits or 7 digits followed by (letter), e.g., 2302679 or 2302679(R)');
      setLoading(false);
      return;
    }

    try {
      const response = await apiPost('/api/register', { 
        name: cleanName, 
        email: cleanEmail, 
        password, 
        shopName: cleanShopName, 
        retailerCode: cleanRetailerCode, 
        address: cleanAddress, 
        licenseNumber: cleanLicenseNumber 
      });

      if (response.ok) {
        // Show success modal
        setShowSuccess(true);
        setLoading(false);
        
        // Redirect after 3 seconds
        setTimeout(() => {
          onLogin();
        }, 3000);
        return; // Don't set loading to false again below
      } else {
        // Handle HTTP error responses
        const errorData = await response.json();
        setError(errorData.message || `Registration failed (${response.status})`);
      }
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    }

    setLoading(false);
  };

  const validation = getPasswordValidation(password);

  return (
    <>
      {/* Success Modal Overlay */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4 animate-in fade-in zoom-in duration-300">
            <div className="text-center">
              <div className="mb-4 flex justify-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Account Created Successfully!
              </h2>
              <p className="text-gray-600 mb-4">
                Welcome to Easy Sheets, Daily! You can now manage your liquor store inventory.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <span>Redirecting to login page</span>
                <div className="animate-spin">⏳</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-2xl shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
              <Wine className="w-8 h-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold">Create Account</CardTitle>
          <CardDescription className="text-base">
            Join Easy Sheets, Daily for your wine shop
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Personal Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border"></div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Personal Information
              </h3>
              <div className="h-px flex-1 bg-border"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocus(true)}
                  onBlur={() => setPasswordFocus(false)}
                  placeholder="Enter a strong password"
                  disabled={loading}
                />
                {(passwordFocus || password) && (
                  <div className="mt-3 p-3 bg-muted rounded-md border">
                    <p className="text-sm font-semibold mb-2">Password Requirements:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-center gap-2 text-sm">
                        {validation.length ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        )}
                        <span className={validation.length ? 'text-green-600' : 'text-red-600'}>
                          8+ characters
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {validation.lowercase ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        )}
                        <span className={validation.lowercase ? 'text-green-600' : 'text-red-600'}>
                          Lowercase (a-z)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {validation.uppercase ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        )}
                        <span className={validation.uppercase ? 'text-green-600' : 'text-red-600'}>
                          Uppercase (A-Z)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {validation.number ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        )}
                        <span className={validation.number ? 'text-green-600' : 'text-red-600'}>
                          Number (0-9)
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Shop Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border"></div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Shop Information
              </h3>
              <div className="h-px flex-1 bg-border"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="shop-name">Shop Name *</Label>
                <Input
                  id="shop-name"
                  type="text"
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  placeholder="Wine Paradise"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="retailer-code">Retailer Code *</Label>
                <Input
                  id="retailer-code"
                  type="text"
                  value={retailerCode}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Remove spaces
                    value = value.replace(/\s/g, '');
                    // Allow only: digits, (, ), and letters A-Z
                    value = value.replace(/[^0-9()A-Za-z]/g, '');
                    // Limit to 10 characters
                    if (value.length <= 10) {
                      setRetailerCode(value);
                    }
                  }}
                  placeholder="e.g., 2302679 or 2302679(R)"
                  maxLength="10"
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  7 digits or 7 digits followed by (letter)
                </p>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Shop Address (Optional)</Label>
                <Textarea
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={3}
                  placeholder="Enter your shop's complete address"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="license-number">License Number (Optional)</Label>
                <Input
                  id="license-number"
                  type="text"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  placeholder="Wine shop license number"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <Button 
            onClick={handleSignup}
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </Button>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Button 
              variant="link" 
              className="p-0 h-auto font-semibold" 
              onClick={onLogin}
            >
              Sign in
            </Button>
          </p>
        </CardFooter>
      </Card>
    </div>
    </>
  );
}

export default Signup;
